import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { db } from "../../firebase";
import { ref, onValue } from "firebase/database";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend,
} from "chart.js";
import { AlertTriangle, ArrowUp, ArrowDown, ChevronLeft,
  Thermometer, Droplets, Wind, Gauge, 
  Volume2, Sun
} from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SENSOR_KEYS = {
  suhu: {label: "Suhu", unit: "°C", icon: <Thermometer className="w-6 h-6"/>},
  kelembapan: {label: "Kelembapan",  unit: "%", icon: <Droplets className="w-6 h-6"/>},
  co2: {label: "Gas CO₂",  unit: "ppm", icon: <Gauge className="w-6 h-6"/>},
  co: {label: "Asap",  unit: "ppm", icon: <Wind className="w-6 h-6"/>},
  kebisingan: {label: "Kebisingan",  unit: "dB", icon: <Volume2 className="w-6 h-6"/>},
  cahaya: {label: "Intensitas Cahaya",unit: "lux", icon: <Sun className="w-6 h-6"/>},
};

export default function DashboardPerKelas() {
  const navigate = useNavigate();
  const { namaKelas } = useParams();
  const [sensorData, setSensorData] = useState(null );
  const [historyData, setHistoryData] = useState({});
  const [ambangBatas, setAmbangBatas] = useState({});
  const [statusKelas, setStatusKelas] = useState("aman");
  const [rekomendasiAktif, setRekomendasiAktif] = useState([]);
  // const [rekomendasiTemplate, setRekomendasiTemplate] = useState(null);
  const [trenSensor, setTrenSensor] = useState({});
  const [prediksiML, setPrediksiML] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const kelasRef = ref(db, `kelas/${namaKelas}`);
    onValue(kelasRef, (snap) => {
      if (snap.exists()) setSensorData(snap.val());
      else setSensorData(null);
    });
    const ambangRef = ref(db, "ambangBatas");
    onValue(ambangRef, (snap) => {
      if (snap.exists()) setAmbangBatas(snap.val());
    });
    // const rekomRef = ref(db, "rekomendasiTindakan");
    // onValue(rekomRef, (snapshot) => {
    //   if (snapshot.exists()) {
    //     setRekomendasiTemplate(snapshot.val());
    //   }
    // });
    // Ambil prediksi: prioritaskan node predictions/{namaKelas}, fallback ambil semua dan filter classId
    const predByClassRef = ref(db, `dataHistoris/${namaKelas}`);
    const unsubPredByClass = onValue(predByClassRef, (snap) => {
      if (snap.exists()) {
        const arr = Object.values(snap.val()).sort((a, b) => a.timestamp - b.timestamp);
        setPrediksiML(arr);
      } else {
        // fallback: ambil semua predictions dan filter by classId === namaKelas
        const allPredRef = ref(db, "dataHistoris");
        onValue(allPredRef, (sAll) => {
          const val = sAll.val() || {};
          const arrAll = Object.keys(val)
            .map((k) => ({ id: k, ...(val[k] || {}) }))
            .filter((it) => {
              if (!it) return false;
              // beberapa struktur menggunakan sensor.classId atau classId field
              const classId = it.classId || (it.sensor && it.sensor.classId) || it.kelas || it.class;
              return String(classId) === String(namaKelas);
            })
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          setPrediksiML(arrAll);
        }, { onlyOnce: false });
      }
    });

    // Ambil data historis per kelas 
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const historyRef = ref(db, `dataHistoris/${today}`);
    onValue(historyRef, (snap) => {
      if (!snap.exists()) {
        setHistoryData({});
        return;
      }

      const all = snap.val();

      // Normalisasi nama kelas
      const normalize = (v) =>
        String(v || "")
          .replace(/kelas/g, "")
          .replace(/\s+/g, "")
          .trim()
          .toLowerCase();

      const target = normalize(namaKelas);

      const filtered = Object.values(all).filter((item) => {
        if (!item || typeof item !== "object") return false;
        const cls =
          item.Kelas ||
          item.kelas ||
          item.class ||
          item.classId ||
          (item.sensor && item.sensor.classId);

        if (!cls) return false;
        return normalize(cls) === target;
      });

      const grouped = {};

      filtered.forEach((item) => {
        if (!item) return;
        const ts = new Date(item.timestamp).getTime();
        if (!ts) return;

        // Ambil dua kemungkinan struktur sensor
        const dataSensor = item.sensor ? item.sensor : item;

        Object.keys(SENSOR_KEYS).forEach((key) => {
          const val = Number(dataSensor[key]);
          if (isNaN(val)) return;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push({ waktu: ts, nilai: val });
        });
      });

      // Sort setiap sensor berdasarkan timestamp
      Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => a.waktu - b.waktu);
      });

      setHistoryData(grouped);
    });

    //mengaktifkan animasi smooth
    setTimeout(() => setIsLoaded(true), 80);
  }, [namaKelas]);

  //menentukan status kelas (gabungan ambang batas +prediksi ML)
  useEffect(() => {
    if (!sensorData) return;

    if (sensorData.status_akhir === 2) setStatusKelas("bahaya");
    else if (sensorData.status_akhir === 1) setStatusKelas("waspada");
    else setStatusKelas("aman");

    // Object.keys(SENSOR_KEYS).forEach((key) => {
    //   const val = sensorData[key];
    //   const batas = ambangBatas[key];
    //   if (val == null || !batas) return;
    //   const status = hitungStatusAmbang(val, batas);
    //   if (status === "bahaya") bahaya = true;
    //   else if (status === "waspada") waspada = true;
    // });

    // setStatusKelas(bahaya ? "bahaya" : waspada ? "waspada" : "aman");
  }, [sensorData]);

  const getStatusLabel = (key) => {
    if (!sensorData) return "Aman";
    // Prioritaskan prediksi ML kelas (jika terakhir menunjukkan level lebih tinggi)
    // if (prediksiML && prediksiML.length > 0) {
    //   const last = prediksiML[prediksiML.length - 1];
    //   if (last && last.prediksi !== undefined) {
    //     if (Number(last.prediksi) === 2) return "Bahaya";
    //     if (Number(last.prediksi) === 1) return "Waspada";
    //     // jika 0 maka fallback ke per-sensor
    //   }
    // }
    const ml = sensorData.prediksi_per_sensor?.[key];

    if (ml === "bahaya") return "Bahaya";
    if (ml === "waspada") return "Tidak Nyaman";
    return "Aman"
    // const val = sensorData[key];
    // const batas = ambangBatas[key];
    // const status = hitungStatusAmbang(val, batas);
    // return status === "bahaya"
    //   ? "Bahaya"
    //   : status === "waspada"
    //   ? "Waspada"
    //   : "Normal";
  };

  const hitungStatusAmbang = (val, batas) => {
    if (!batas) return "aman";

    const cekRange = (obj) =>
      val >= Number(obj.min) && val <= Number(obj.max);

      // kritis dulu
      if (batas.bahaya && cekRange(batas.bahaya)) return "bahaya";
      if (batas.bahayabatasbawah && cekRange(batas.bahayabatasbawah)) return "bahaya";
      if (batas.bahayabatasatas && cekRange(batas.bahayabatasatas)) return "bahaya";

      // peringatan
      if (batas.waspada && cekRange(batas.waspada)) return "waspada";
      if (batas.waspadabatasbawah && cekRange(batas.waspadabatasbawah)) return "waspada";
      if (batas.waspadabatasatas && cekRange(batas.waspadabatasatas)) return "waspada";

      return "aman";
  };

  const getColor = (status) => {
    switch (status) {
      case "Bahaya":
        return "text-red-600";
      case "Tidak Nyaman":
        return "text-yellow-600";
      default:
        return "text-green-600";
    }
  };

  // tren sensor
  useEffect(() => {
    const tren = {};
    Object.keys(historyData).forEach((key) => {
      const logs = historyData[key] ? historyData[key] : [];
      if (logs.length >= 2) {
        const sorted = [...logs].sort((a, b) => new Date(a.waktu) - new Date(b.waktu));
        const latest = sorted[sorted.length - 1];
        //ambil data 1 jam lalu (jika ada)
        const oneHourAgo = sorted.slice().reverse()
          .find((d) => new Date(latest.waktu) - new Date(d.waktu) >= 60 * 60 * 1000);
        if (oneHourAgo) {
          const diff = latest.nilai - oneHourAgo.nilai;
          tren[key] = diff;
        } else {
          tren[key] = 0;
        }
      }
    });
    setTrenSensor(tren);
  }, [historyData]);

  useEffect(() => {
    if (!sensorData) return;
    // const aktif = [];
    // filter rekomendasi sesuai nilai sensor yang melampaui batas
    // const aktif = rekomendasiTindakan.filter(({ kondisi }) => {
    //   const val = sensorData[kondisi];
    //   const batas = ambangBatas[kondisi];
    //   if (!val || !batas) return false;
    //   return val > batas; // munculkan jika melewati ambang batas
    // });
    // Object.keys(SENSOR_KEYS).forEach((key) => {
    //   const val = sensorData[key];
    const rekomML = sensorData.rekomendasi_ml || [];
    

    console.log(sensorData.rekomendasi_ml);
    setRekomendasiAktif(rekomML);
  }, [sensorData]);

  // cari nilai prediksi paling dekat berdasarkan waktu label (waktu string/timestamp)
  const NilaiPediksiLabel = (labelTimeStr, prediksiArr) => {
    // labelTimeStr dari logs: biasanya "HH:MM" (slice(11,16))
    // convert label to minutes since midnight
    const [lh, lm] = String(labelTimeStr).split(":").map((v) => Number(v));
    if (isNaN(lh)) return null;
    const labelMinutes = lh * 60 + (isNaN(lm) ? 0 : lm);

    // cari prediksi dengan closest timestamp hour:minute
    let best = null;
    let bestDiff = Infinity;
    prediksiArr.forEach((p) => {
      const t = Number(p.timestamp);
      if (!t) return;
      const d = new Date(t);
      const pm = d.getHours() * 60 + d.getMinutes();
      const diff = Math.abs(pm - labelMinutes);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = p;
      }
    });
    // threshold: jika terlalu jauh, kita masih kembalikan best (jika diperlukan)
    return best ? Number(best.prediksi) : null;
  };

  const chartConfig = (key) => {
    const logs = [...(historyData[key] || [])]
      .filter(d => d.waktu && !isNaN(d.nilai))
      .sort((a, b) => a.waktu - b.waktu)
      .slice(-30);

    const labels = logs.map((d) => {
      const t = new Date(d.waktu);
      return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`
    });

    return {
      labels,
      datasets: [
        {
          label: key,
          data: logs.map(d => d.nilai),
          borderColor: "#2563eb",
          backgroundColor: "rgba(37,99,235,0.1)",
          borderWidth: 2,
          tension: 0.35,
          pointRadius: 3,
          spanGaps: true
        }
      ]
    };
  };

  return (
    <div className={`min-h-screen bg-slate-50 transition-all duration-500 ${isLoaded ? "opacity-100" : "opacity-0"}`}>
      {/* Header */}
      <div className="bg-emerald-600 shadow p-4 flex justify-between items-center sticky top-0 z-10">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center px-3 py-1.5 text-lg font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="absolute left-1/2 -translate-x-1/2 w-full text-lg text-center text-white font-semibold pointer-events-none">
          Dashboard {namaKelas}
        </h2>
      </div>

      {/* Loading state */}
      {(sensorData === null || ambangBatas === null || Object.keys(ambangBatas).length === 0) ? (
        <div className="flex justify-center items-center h-64 text-gray-500">
          Memuat data sensor...
        </div>
      ) : (
        <>
          {/* Alert */}
          {statusKelas !== "aman" && (
            <div
              className={`text-white p-4 text-sm text-center font-semibold transition-all duration-300 ${
                statusKelas === "bahaya" ? "bg-red-600" : "bg-yellow-500"
              }`}
            >
              <AlertTriangle className="inline-block w-5 h-5 mr-2" />
              PERINGATAN! Kondisi lingkungan ruang kelas tidak optimal.
            </div>
          )}

          {/* Konten */}
          <div className="max-w-7xl mx-auto w-full p-4 sm:p-6 lg:p-8">
            {/* Grid sensor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {Object.entries(SENSOR_KEYS).map(([key, {label, unit}]) => {
                const val = sensorData[key];
                if (val == null) return null;
                const status = getStatusLabel(key);
                const diff = trenSensor[key] || 0;
                const arrowIcon =
                  diff > 0 ? (
                    <ArrowUp className="inline-block text-red-500 w-4 h-4" />
                  ) : diff < 0 ? (
                    <ArrowDown className="inline-block text-green-500 w-4 h-4" />
                  ) : null;
                
                return (
                  <div key={key}
                    className="bg-white p-5 rounded-2xl shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-700 capitalize text-base sm:text-lg">
                        {label}
                      </h3>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          status === "Bahaya"
                            ? "bg-red-100 text-red-600"
                            : status === "Tidak Nyaman"
                            ? "bg-yellow-100 text-yellow-600"
                            : "bg-green-100 text-green-600"
                        }`}
                      >
                        {status}
                      </span>
                    </div>

                    <p className={`text-3xl sm:text-4xl font-bold ${getColor(status)}`}>
                      {sensorData[key]} {unit}
                    </p>

                    {/* Perbandingan dengan 1 jam lalu */}
                    {historyData[key] && Object.values(historyData[key]).length > 2 && (
                      (() => {
                        const logs = Object.values(historyData[key]);
                        const nowVal = logs[logs.length - 1].nilai;
                        // ambil nilai kira-kira 1 jam lalu (60 data jika 1 menit sekali)
                        const pastVal = logs.length > 60 ? logs[logs.length - 61].nilai : logs[0].nilai;
                        const diff = nowVal - pastVal;
                        const trend = diff > 0 ? "naik" : diff < 0 ? "turun" : "stabil";
                        // const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
                        return (
                          // {arrowIcon && (
                            <p className="text-sm text-gray-600 mt-1">
                              {arrowIcon}{" "}
                              {Math.abs(diff).toFixed(1)}
                              {" "}{unit} dari 1 jam lalu ({trend})
                            </p>
                          // )}
                          // <p className="text-sm text-gray-500 mt-1">
                          //   {arrow} {Math.abs(diff).toFixed(1)} dari 1 jam lalu ({trend})
                          // </p>
                        );
                      })()
                    )}

                    {/* Indikator tren */}
                    

                    {/* Grafik */}
                    <div className="mt-4" style={{ height: "160px" }}>
                      <Line
                        data={chartConfig(key)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          animation: { duration: 400 },
                          plugins: { legend: { display: false } },
                          scales: {
                            y: { beginAtZero: true },
                            x: {},
                          },
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Rekomendasi Tindakan*/}
            {rekomendasiAktif.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-md transition-all">
                <h3 className="text-lg sm:text-xl font-bold text-gray-700 mb-4">
                  💡 Rekomendasi Tindakan
                </h3>
                <ul className="space-y-3 text-gray-700 text-sm sm:text-base">
                  {rekomendasiAktif.map((r, i) => {
                    const style =
                      r.status === "bahaya"
                        ? "bg-red-50 border-red-300 text-red-700"
                        : "bg-yellow-50 border-yellow-300 text-yellow-700";

                    const iconColor =
                      r.status === "bahaya"
                        ? "text-red-500"
                        : "text-yellow-500";

                    return (
                      <li
                        key={i}
                        className={`flex items-start gap-3 p-3 border rounded-xl transition-all ${style}`}
                      >
                        <AlertTriangle className={`${iconColor} w-4 h-4 mt-1`} />
                        <div>
                          <p className="font-semibold">
                            {r.judul}
                          </p>
                          <p className="text-sm">
                            {r.aksi}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}